import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface PlantForm {
  name: string;
  variety?: string;
  location: {
    description: string;
  };
}

interface Props {
  onComplete: () => void;
}

const StartWithPlant: React.FC<Props> = ({ onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PlantForm>();

  const onSubmit = async (data: PlantForm) => {
    setLoading(true);
    setError('');

    try {
      await api.post('/onboarding/start-with-plant', data);
      toast.success('🌱 Your first plant has been added!');
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create plant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>
          🌿
        </Typography>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Add Your First Plant
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Let's start with a single plant. You can add more later!
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          label="Plant Name"
          margin="normal"
          {...register('name', {
            required: 'Please name your plant',
          })}
          error={!!errors.name}
          helperText={errors.name?.message || 'e.g., "Basil", "Spider Plant", "Tomato"'}
        />

        <TextField
          fullWidth
          label="Variety (Optional)"
          margin="normal"
          {...register('variety')}
          helperText='e.g., "Genovese Basil", "Cherry Tomato"'
        />

        <TextField
          fullWidth
          label="Where is it located?"
          margin="normal"
          {...register('location.description', {
            required: 'Please describe where your plant is',
          })}
          error={!!errors.location?.description}
          helperText={
            errors.location?.description?.message ||
            'e.g., "Kitchen windowsill", "Living room corner", "Balcony"'
          }
        />

        <Box mt={4}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create My First Plant'}
          </Button>
        </Box>
      </form>

      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          💡 Tip: You can organize your plants into gardens later as your collection grows!
        </Typography>
      </Box>
    </Paper>
  );
};

export default StartWithPlant;